/**
 * HeartFlow — Flow controller for the Heart Flow & Warmth experience
 * 
 * Steps: Entry → Heart Arrival → Positioning → Affirmation Preview → Immersion → Completion
 */

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import HealingEntry from './HealingEntry';
import HeartArrival from './HeartArrival';
import PositioningGuide from './PositioningGuide';
import HeartAffirmations from './HeartAffirmations';
import HeartImmersion from './HeartImmersion';
import HealingCompletion from './HealingCompletion';
import useAudioMode from '../../hooks/useAudioMode';

const API = process.env.REACT_APP_BACKEND_URL;

const CONDITION = 'heart-flow';
const CONDITION_LABEL = 'Heart Flow & Warmth';

const HeartFlow = () => {
  const navigate = useNavigate();
  const [, setMode] = useAudioMode();
  const [step, setStep] = useState(0);
  const [stepsCompleted, setStepsCompleted] = useState([]);
  const [startTime] = useState(Date.now());
  const selectedAffirmationsRef = useRef([]);

  const markStep = useCallback((stepName) => {
    setStepsCompleted(prev => [...new Set([...prev, stepName])]);
  }, []);

  const handleSave = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    const duration = Math.round((Date.now() - startTime) / 1000);
    try {
      await fetch(`${API}/api/healing/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          condition: CONDITION,
          duration_seconds: duration,
          steps_completed: stepsCompleted,
        }),
      });
    } catch (e) {
      console.error('Failed to save session:', e);
    }
  }, [startTime, stepsCompleted]);

  return (
    <AnimatePresence mode="wait">
      {step === 0 && (
        <HealingEntry
          key="entry"
          title={CONDITION_LABEL}
          subtitle="A soft warmth begins to gather..."
          description="gently opening the space within"
          onBegin={(mode) => { setMode(mode); setStep(3); }}
          onBack={() => navigate('/healing')}
          area="healing_heart"
          glow="rgba(238,140,108,0.62)"
          accent="rgba(220,160,130,0.6)"
        />
      )}

      {step === 1 && (
        <HeartArrival
          key="arrival"
          onComplete={() => { markStep('arrival'); setStep(2); }}
          onSkip={() => setStep(2)}
        />
      )}

      {step === 2 && (
        <PositioningGuide
          key="positioning"
          onReady={() => { markStep('positioning'); setStep(3); }}
        />
      )}

      {step === 3 && (
        <HeartAffirmations
          key="affirmations"
          onContinue={(affirmations) => {
            selectedAffirmationsRef.current = affirmations;
            markStep('affirmations');
            setStep(4);
          }}
        />
      )}

      {step === 4 && (
        <HeartImmersion
          key="immersion"
          affirmations={selectedAffirmationsRef.current}
          onComplete={() => { markStep('immersion'); setStep(5); }}
        />
      )}

      {step === 5 && (
        <HealingCompletion
          key="completion"
          onSave={() => { handleSave(); }}
          onRepeat={() => setStep(3)}
          onHome={() => navigate('/healing')}
        />
      )}
    </AnimatePresence>
  );
};

export default HeartFlow;
